import { createFileRoute, notFound } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"

import { LocationSearchForm } from "@/components/homepage/location-search-form"
import { MarketCompanyCard } from "@/components/market/market-company-card"
import { MarketPagination } from "@/components/market/market-pagination"
import { SharedFooter } from "@/components/shared-footer"
import { WBSection } from "@/components/wb-ui/wb-section"
import { dataByState, stateCodeBySlug } from "@/constants/state"
import { isIndexableMarket } from "@/helpers/market-helpers"

const fetchMarketPageData = createServerFn({ method: "GET" })
  .inputValidator(
    (data: unknown) =>
      data as { stateSlug: string; marketSlug: string; splat: string }
  )
  .handler(async ({ data }) => {
    const { fetchMarketWithCompanies } = await import("@/db/queries/market")
    const { stateSlug, marketSlug, splat } = data
    const stateCode = stateCodeBySlug[stateSlug]
    if (!stateCode) return null

    const pageNum = splat ? parseInt(splat, 10) : 1
    if (isNaN(pageNum) || pageNum < 1) return null

    const result = await fetchMarketWithCompanies(
      stateCode,
      marketSlug,
      pageNum
    )
    if (!result) return null

    const { market, companies, totalCompanies, pageSize } = result
    const stateName =
      dataByState[stateCode as keyof typeof dataByState]?.name ?? stateCode
    const totalPages = Math.ceil(totalCompanies / pageSize)
    const baseUrl = `/solar-installers/${stateSlug}/${marketSlug}`
    const shouldIndex = isIndexableMarket(stateCode, market.pool_size)

    return {
      market,
      companies,
      totalCompanies,
      stateName,
      stateSlug,
      marketSlug,
      currentPage: pageNum,
      totalPages,
      baseUrl,
      shouldIndex,
    }
  })

export const Route = createFileRoute("/solar-installers/$state/$marketSlug/$")({
  loader: async ({ params }) => {
    const result = await fetchMarketPageData({
      data: {
        stateSlug: params.state,
        marketSlug: params.marketSlug,
        splat: params._splat,
      },
    })
    if (!result) throw notFound()
    return result
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {}
    const {
      market,
      stateName,
      stateSlug,
      marketSlug,
      currentPage,
      totalPages,
      totalCompanies,
      shouldIndex,
    } = loaderData

    const baseTitle = `Best Solar Installers in ${market.market_name}, ${stateName} | WattBot`
    const title =
      currentPage > 1 ? `${baseTitle} — Page ${currentPage}` : baseTitle
    const description = `Compare the top ${totalCompanies} solar installers in ${market.market_name}. See trust scores, verified reviews, and ratings.`
    const fullBaseUrl = `https://www.wattbot.com/solar-installers/${stateSlug}/${marketSlug}`
    const canonical =
      currentPage > 1 ? `${fullBaseUrl}/${currentPage}` : fullBaseUrl

    const links: Array<{ rel: string; href: string }> = [
      { rel: "canonical", href: canonical },
    ]
    if (currentPage > 1) {
      links.push({
        rel: "prev",
        href:
          currentPage === 2 ? fullBaseUrl : `${fullBaseUrl}/${currentPage - 1}`,
      })
    }
    if (currentPage < totalPages) {
      links.push({ rel: "next", href: `${fullBaseUrl}/${currentPage + 1}` })
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        {
          name: "robots",
          content:
            !shouldIndex || currentPage > 1
              ? "noindex, follow"
              : "index, follow",
        },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: canonical },
        { property: "og:image", content: "/og-images/og-reports.png" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: "/og-images/og-reports.png" },
      ],
      links,
    }
  },
  component: MarketPage,
})

function MarketPage() {
  const { market, companies, stateName, currentPage, totalPages, baseUrl } =
    Route.useLoaderData()

  return (
    <div className="bg-muted flex min-h-screen flex-col">
      <main className="mx-auto w-full max-w-screen-md flex-1 p-4 sm:p-6 lg:p-8">
        <nav className="text-muted-foreground mb-2 flex items-center gap-2 font-mono text-sm">
          <span>USA</span>
          <span>/</span>
          <span>{stateName}</span>
        </nav>
        <h1 className="wb-h2 leading-tight">
          Best Solar Installers <br />
          <div className="bg-brand text-foreground -mx-1 inline-block -rotate-1 px-1">
            {market.market_name}
          </div>
        </h1>
        <p className="mt-2 font-mono">
          {`${market.pool_size} companies analyzed in your area`}
        </p>

        <div className="mt-8 flex flex-col gap-6">
          {companies.map((company) => (
            <MarketCompanyCard
              key={company.longSlug}
              company={company}
              description={company.narrative}
            />
          ))}
        </div>

        {companies.length === 0 && (
          <p className="text-muted-foreground py-8 text-center font-mono text-sm">
            No ranked installers in this market yet.
          </p>
        )}

        {totalPages > 1 && (
          <MarketPagination
            currentPage={currentPage}
            totalPages={totalPages}
            baseUrl={baseUrl}
          />
        )}

        <WBSection title="Find More Markets">
          <div className="max-w-xl">
            <LocationSearchForm />
          </div>
        </WBSection>
      </main>
      <SharedFooter />
    </div>
  )
}
